import React, { useState, useEffect, forwardRef } from 'react';
import MaterialTable from 'material-table';
import { useAuth } from '../../context/auth';
import { useMessage } from '../../context/message';
import { useField } from '../../context/context';
import CustomFilter from '../CustomFilter';
import RefreshIcon from '@material-ui/icons/Refresh';
import VerticalAlignBottomIcon from '@material-ui/icons/VerticalAlignBottom';

export default function RailcarRates() {
  const [data, setData] = useState([]);
  const [toggle, setToggle] = useState(false);
  const { auth } = useAuth();
  const { setMessage } = useMessage();
  const { product_categories, products } = useField();

  useEffect(() => {
    fetch('/api/railcar_rates/all', {
      method: 'GET',
      headers: { 'x-access-token': sessionStorage.getItem('token') }
    })
    .then(response => auth(response))
    .then(response => setData(response.data || []))
    .catch(error => {
      setMessage({ type: 'error', text: 'Error! Unable to fetch railcar rates' });
      console.error('Error:', error);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toggle]);

  const handleAdd = newData => new Promise((resolve, reject) => {
    fetch('/api/railcar_rates', {
      method: 'POST',
      headers: {
        'x-access-token': sessionStorage.getItem('token'),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(newData)
    })
    .then(response => auth(response))
    .then(response => {
      setMessage(response.message);
      if (response.message.type === 'success') { setToggle(t => !t); resolve(); }
      else reject();
    })
    .catch(error => {
      setMessage({ type: 'error', text: 'Error! Unable to create railcar rate' });
      console.error('Error:', error);
      reject();
    });
  });

  const handleUpdate = (newData, oldData) => new Promise((resolve, reject) => {
    fetch(`/api/railcar_rates/${oldData.railcar_id}`, {
      method: 'PUT',
      headers: {
        'x-access-token': sessionStorage.getItem('token'),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(newData)
    })
    .then(response => auth(response))
    .then(response => {
      setMessage(response.message);
      if (response.message.type === 'success') { setToggle(t => !t); resolve(); }
      else reject();
    })
    .catch(error => {
      setMessage({ type: 'error', text: 'Error! Unable to update railcar rate' });
      console.error('Error:', error);
      reject();
    });
  });

  const handleDelete = oldData => new Promise((resolve, reject) => {
    fetch(`/api/railcar_rates/${oldData.railcar_id}`, {
      method: 'DELETE',
      headers: { 'x-access-token': sessionStorage.getItem('token') }
    })
    .then(response => auth(response))
    .then(response => {
      setMessage(response.message);
      if (response.message.type === 'success') { setToggle(t => !t); resolve(); }
      else reject();
    })
    .catch(error => {
      setMessage({ type: 'error', text: 'Error! Unable to delete railcar rate' });
      console.error('Error:', error);
      reject();
    });
  });

  const productCategoryLookup = (product_categories || []).reduce((lookup, item) => {
    lookup[item.product_category] = item.product_category;
    return lookup;
  }, {});

  const productLookup = (products || []).reduce((lookup, item) => {
    lookup[item.product] = item.product;
    return lookup;
  }, {});

  const columns = [
    { title: 'Railcar ID', field: 'railcar_id', editable: 'never' },
    { title: 'Company', field: 'company' },
    { title: 'Inquiry', field: 'inquiry' },
    { title: 'Min Railcars', field: 'min_railcars', type: 'numeric' },
    { title: 'Max Railcars', field: 'max_railcars', type: 'numeric' },
    { title: 'Origin', field: 'origin' },
    { title: 'Destination', field: 'destination' },
    { title: 'Date Placed', field: 'date_placed', type: 'datetime', editable: 'never' },
    { title: 'Start Date', field: 'start_date', type: 'date' },
    { title: 'Price', field: 'price', type: 'numeric' },
    { title: 'Type', field: 'type' },
    {
      title: 'Request Avail',
      field: 'request_avail',
      lookup: {
        Railcar_Demand: 'Railcar Demand',
        Railcar_Availability: 'Railcar Availability',
        Trade: 'Trade',
      },
    },
    {
      title: 'Category',
      field: 'category',
      lookup: { GP: 'GP', Pressure: 'Pressure' },
    },
    { title: 'Product', field: 'product' },
    { title: 'Product Category', field: 'product_category', lookup: productCategoryLookup },
    { title: 'Term', field: 'term', type: 'numeric' },
    { title: 'Class One', field: 'class_one' },
    { title: 'Coiled/Insulated', field: 'coiled_insulated', lookup: { 0: 'N', 1: 'Y' } },
    { title: 'Size', field: 'size', type: 'numeric' },
    { title: 'TT Product', field: 'tt_product', lookup: productLookup },
  ];

  return (
    <MaterialTable
      title='Railcar Rates'
      columns={columns}
      data={data}
      icons={{ Export: forwardRef((props, ref) => <VerticalAlignBottomIcon color='primary' {...props} ref={ref} />) }}
      options={{
        filtering: true,
        draggable: false,
        exportButton: true,
        exportAllData: true,
        exportFileName: 'railcar_rates',
      }}
      components={{ FilterRow: props => <CustomFilter {...props} /> }}
      actions={[
        {
          icon: () => <RefreshIcon color='primary' />,
          tooltip: 'Refresh',
          isFreeAction: true,
          onClick: () => setToggle(t => !t)
        }
      ]}
      editable={{
        onRowAdd: handleAdd,
        onRowUpdate: handleUpdate,
        onRowDelete: handleDelete
      }}
    />
  );
}
